import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { SortableTable, type Column } from '@/components/SortableTable'
import { useIsPhone } from '@/lib/useMediaQuery'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { mobileUsersMessages } from '@/i18n/messages/mobileUsers'
import { btnPrimary, btnSecondary, inputCls } from '@/features/users/parts'
import { CreateMobileUserDialog, EditMobileUserDialog } from './EditMobileUserDialog'
import { FILTERS, TITLES, fullName, type MobileOptions, type MobileUser } from './types'

/**
 * AU's Mobile users page: Flutter-app logins. Edit opens a modal with every field.
 */
export function MobileUsersPage() {
  const t = useT(mobileUsersMessages)
  const tc = useT(commonMessages)
  const phone = useIsPhone()
  const [q, setQ] = useState('')
  const [title, setTitle] = useState(999)
  const [editing, setEditing] = useState<MobileUser | null>(null)
  const [creating, setCreating] = useState<'user' | 'translation' | null>(null)

  const list = useQuery<MobileUser[]>({ queryKey: ['mobile-users'], queryFn: () => api.get<MobileUser[]>('/api/mobile-users/') })
  const options = useQuery<MobileOptions>({ queryKey: ['mobile-users-options'], queryFn: () => api.get<MobileOptions>('/api/mobile-users/options') })

  const all = list.data ?? []
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return all.filter((u) =>
      (title === 999 || u.titleSelector === title) &&
      (!needle || [fullName(u), u.username, u.personalNumber, u.employeeName, u.employeeNumber, u.phone, u.countryName]
        .some((v) => v?.toLowerCase().includes(needle))))
  }, [all, q, title])

  const titleLabel = (id: number) => {
    const hit = TITLES.find((x) => x.id === id)
    return hit ? t(hit.key) : String(id)
  }
  const loader = usePageLoader(list.isPending, t('loading'))
  if (loader) return loader
  if (list.error) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{tc('couldNotLoad', { what: t('title') })}</p>

  const writes = !!options.data?.enabled
  const columns: Column<MobileUser>[] = [
    {
      key: 'name', header: t('colUser'),
      sortValue: (u) => fullName(u).toLowerCase(),
      render: (u) => (
        <div className="min-w-0">
          <div className="font-medium">{fullName(u) || '—'}</div>
          <div className="truncate font-mono text-xs text-neutral-400">{u.username}{u.active ? '' : ` · ${t('off')}`}</div>
        </div>
      ),
    },
    { key: 'number', header: t('colNumber'), className: 'w-28', sortValue: (u) => u.personalNumber ?? '', render: (u) => u.personalNumber ?? '—' },
    {
      key: 'title', header: t('colTitle'), className: 'w-36',
      sortValue: (u) => titleLabel(u.titleSelector),
      render: (u) => (
        <span>
          {titleLabel(u.titleSelector)}
          {u.isAsfalt === 1 ? <span className="ml-1 text-xs text-neutral-400">({t('asphalt')})</span> : null}
        </span>
      ),
    },
    { key: 'phone', header: t('colPhone'), className: 'w-32', sortValue: (u) => u.phone ?? '', render: (u) => u.phone ?? '—' },
    { key: 'country', header: t('colCountry'), className: 'w-28', sortValue: (u) => (u.countryName ?? '').toLowerCase(), render: (u) => u.countryName ?? '—' },
    { key: 'employee', header: t('colEmployee'), sortValue: (u) => (u.employeeName ?? '').toLowerCase(), render: (u) => u.employeeName ?? '—' },
    { key: 'rtk', header: t('colRtk'), className: 'w-32', sortValue: (u) => (u.rtkLoginName ?? '').toLowerCase(), render: (u) => u.rtkLoginName ?? '—' },
    { key: 'app', header: t('colApp'), className: 'w-24', sortValue: (u) => u.lastAppVersion ?? '', render: (u) => u.lastAppVersion ?? '—' },
    {
      key: 'edit', header: '', sticky: 'right', className: 'w-16 text-right',
      render: (u) => (
        <button type="button" onClick={() => setEditing(u)}
          className="rounded-md px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/30 hover:bg-brand-50 dark:text-brand-100 dark:ring-brand-100/20 dark:hover:bg-brand-700/20">
          {tc('edit')}
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="mt-1 max-w-prose text-sm text-neutral-500">{t('intro')}</p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <input className={`${inputCls} max-w-xs`} type="search" value={q} placeholder={t('search')} onChange={(e) => setQ(e.target.value)} />
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" onClick={() => setTitle(f.id)} aria-pressed={title === f.id}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${title === f.id ? 'bg-neutral-900 text-white ring-neutral-900 dark:bg-white dark:text-neutral-900 dark:ring-white' : 'text-neutral-600 ring-neutral-300 hover:bg-neutral-50 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800'}`}>
              {t(f.key)}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button type="button" onClick={() => setCreating('translation')} disabled={!writes} title={writes ? undefined : t('writesOff')} className={btnSecondary}>{t('addTranslation')}</button>
          <button type="button" onClick={() => setCreating('user')} disabled={!writes} title={writes ? undefined : t('writesOff')} className={btnPrimary}>{t('addUser')}</button>
        </div>
      </div>
      {options.data && !writes && <p className="text-xs text-neutral-500">{t('writesOff')}</p>}

      {phone ? (
        <ul className="space-y-2">
          {rows.length === 0 && <li className="text-sm text-neutral-500">{t('empty')}</li>}
          {rows.map((u) => (
            <li key={u.workerId} className="rounded-2xl bg-white p-3 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium">{fullName(u) || '—'}</div>
                  <div className="text-xs text-neutral-500">{titleLabel(u.titleSelector)}{u.isAsfalt === 1 ? ` · ${t('asphalt')}` : ''}{u.active ? '' : ` · ${t('off')}`}</div>
                  <div className="mt-0.5 truncate font-mono text-xs text-neutral-400">{u.username}</div>
                  {(u.employeeName || u.phone) && <div className="mt-0.5 truncate text-xs text-neutral-500">{[u.employeeName, u.phone].filter(Boolean).join(' · ')}</div>}
                </div>
                <button type="button" onClick={() => setEditing(u)}
                  className="rounded-md px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/30">{tc('edit')}</button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <SortableTable columns={columns} rows={rows} rowKey={(u) => u.workerId} initialSort={{ key: 'name', dir: 'asc' }} empty={t('empty')} fixed />
      )}

      {editing && options.data && <EditMobileUserDialog user={editing} options={options.data} onClose={() => setEditing(null)} />}
      {creating && options.data && (
        <CreateMobileUserDialog options={options.data} translation={creating === 'translation'} onClose={() => setCreating(null)} />
      )}
    </div>
  )
}
